import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataDistributionCentersDTO } from "@src/domain/dto/masterData";
import type { MasterDataDistributionCenter } from "@src/domain/entities/masterData";
import { GetMasterDataDistributionCentersUseCase } from "@src/application/useCases/masterData";
import { MasterDataDistributionCenterRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

interface MasterDataDistributionCentersDataContextValue {
  brandKey: string;
  distributionCenters: MasterDataDistributionCenter[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataDistributionCentersDataContext =
  createContext<MasterDataDistributionCentersDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataDistributionCenters(): MasterDataDistributionCentersDataContextValue {
  const context = useContext(MasterDataDistributionCentersDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataDistributionCenters must be used within MasterDataDistributionCentersDataProvider",
    );
  }
  return context;
}

interface MasterDataDistributionCentersDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

/**
 * Catalog-aligned flow: Repository → UseCase → Provider context → UI hooks.
 * Tenant DB is selected via X-Brand-Key on MasterDataGraphQlAdapter.
 */
export function MasterDataDistributionCentersDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataDistributionCentersDataProviderProps>) {
  const getDistributionCentersUseCase = useMemo(
    () =>
      new GetMasterDataDistributionCentersUseCase(
        new MasterDataDistributionCenterRepository(),
      ),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getDistributionCentersUseCase.execute(
        new GetMasterDataDistributionCentersDTO({
          search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getDistributionCentersUseCase],
  );

  const {
    items: distributionCenters,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data distribution centers",
    cacheScope: "distribution-centers",
    fetchPage,
  });

  const value = useMemo(
    (): MasterDataDistributionCentersDataContextValue => ({
      brandKey,
      distributionCenters,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      distributionCenters,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataDistributionCentersDataContext.Provider value={value}>
      {children}
    </MasterDataDistributionCentersDataContext.Provider>
  );
}
